import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  TouchableOpacity,
  ImageBackground,
  Image,
  ImageSourcePropType,
  useWindowDimensions,
  ScrollView,
  ActivityIndicator,
  ViewStyle,
  TextStyle,
  ImageStyle,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from './ui';
import { fonts, typography } from '../styles';
import type { EventKind } from '../hooks/useEventThread';
import { favoriteIconName, favoriteA11yLabel } from './favoriteIcon';
import { resolveEndedHeroCopy } from './liveEndedHeroCopy';
import type { RecordingOutcome } from '../screens/live/recordingOutcome';
import { useCookieBannerInset } from './cookieBannerInset';
import { HEADER_ZONE_ID, useReservedZoneHeight } from '../widget/reservedZones';
import { fontWeights } from '../styles/typographyTokens';
import { AgapellaBadge } from './subscription/AgapellaBadge';
import type { ContentAccess } from '../types/agapellaSubscription';
import { ON_ACCENT_TEXT } from '../styles/accentText';

/**
 * Запасная высота шапки — на первый кадр, до её первого замера.
 *
 * CORE-1116 — фактическую высоту шапка объявляет сама (CORE-1120), и с этой
 * задачи она может быть больше: над навигацией встаёт полоса с предложением
 * поставить приложение. Зашитое число оставило бы верх страницы под ней.
 */
const HEADER_HEIGHT_FALLBACK = Platform.OS === 'web' ? 90 : 80;

type MasterSummary = {
  id: number;
  name: string;
  photoUrl: string | null;
};

export type LiveAnnouncementHeroProps = {
  liveId: number;
  /** Event kind — drives pill label, CTA behaviour, and chat thread payload. */
  eventKind?: EventKind;
  title: string;
  description: string | null;
  startDateTimeUtc: string;
  durationMinutes?: number | null;
  master: MasterSummary;
  bgImageUri?: string | null;
  /**
   * Master avatar. Accepts a URL string (backend-hosted) OR a local asset
   * via `require('./logo.png')` — the latter is used for community branding.
   */
  masterAvatarUrl?: string | ImageSourcePropType | null;

  isEnded: boolean;
  isLive: boolean;
  isOwner: boolean;
  /** Owner-only edit affordances (title pencil / «Редактировать»). Default
   *  true. Pass false where editing isn't a feature (scheduled rooms have
   *  no edit modal — the announcement screen *is* the management surface),
   *  so the pencil isn't a dead no-op (AMB-152). */
  canEdit?: boolean;
  /**
   * CORE-1149 — этот эфир ведёт назначенный человек, и он смотрит на страницу
   * из своего личного аккаунта. Ему нужна ровно одна кнопка владельца — вход в
   * студию; публикация, редактирование и удаление события остаются у профиля.
   */
  canHost?: boolean;
  isAuthenticated: boolean;

  /**
   * For live rooms (and optionally livestreams): when `true`, the primary CTA
   * becomes "Войти в комнату" (green) instead of "Я иду" (orange) — user can
   * enter now instead of RSVPing for later.
   */
  canJoinNow?: boolean;
  onJoinNow?: () => void;

  /**
   * CORE-1435 — до окна входа «Присоединиться» и «Напомнить о начале» — две
   * разные кнопки: первая ведёт на экран подготовки, вторая включает
   * напоминание. Раскладка включается, только когда переданы оба обработчика
   * (RoomAnnouncementScreen); без них (LiveDetailScreen) остаётся прежняя одна
   * кнопка RSVP на `onSubscribeToggle`.
   */
  onJoinBeforeStart?: () => void;
  onRemindToggle?: () => void;
  /**
   * CORE-1435 — время начала наступило, а ведущий ещё не запустил сессию.
   * Вместо отсчёта «через 0 секунд» герой говорит, что сессия вот-вот начнётся.
   */
  isAwaitingHost?: boolean;

  /**
   * For ended events, describes *why* no recording is playable on this screen.
   * - 'completed'       → live happened, no recording available (default)
   * - 'cancelled'       → live did not take place
   * - 'subscribers-only'→ recording exists but is restricted to master subscribers
   * - 'attendees-only'  → recording exists but is restricted to registered attendees
   * - 'agapella'        → CORE-1778: the recording is included in the Agapēlla subscription
   */
  endedVariant?: 'completed' | 'cancelled' | 'subscribers-only' | 'attendees-only' | 'agapella';
  /** CORE-1778 — `access` эфира с сервера: бейдж «Входит в Agapēlla» в герое. */
  access?: ContentAccess | null;
  /** CORE-1778 — закрыто подпиской Agapēlla: кнопка открывает окно. */
  onOpenAgapellaPaywall?: () => void;

  /**
   * True when the host failed to start the broadcast in time and the system
   * auto-ended the event (>5 min after the scheduled start, no recording).
   * Overrides the «cancelled / no recording» copy with a polite, named
   * apology for viewers: «{Master} не смог подключиться».
   * The host gets their OWN honest copy — «{Event} не состоялась» + no
   * recording promise + a next step (CORE-981). Previously the host fell into
   * the generic «Спасибо за эфир!» + «Запись готовится» flow, which thanked
   * them for an event that never happened and promised a recording that would
   * never arrive.
   */
  hostNoShow?: boolean;

  /**
   * For ended events: true once a published recording is available for playback.
   * Drives the post-broadcast copy (Twitch/YouTube «Past streams» pattern):
   *   - true  → «Запись готова» + «Смотреть запись» CTA
   *   - false → «Запись готовится» (processing placeholder)
   * Defaults to false so existing call sites without recording info keep the
   * pre-existing «no recording» messaging.
   */
  hasRecording?: boolean;
  /**
   * CORE-1526 (QA FAIL 05.10) — что случилось с записью, когда файла нет:
   * собирается, не получилась или так и не появилась. Без исхода владельцу
   * обещали обработку «обычно 5–15 минут» в любом из трёх случаев.
   */
  recordingOutcome?: RecordingOutcome;
  /**
   * Owner-only: handler for the «Смотреть запись» CTA on the post-broadcast
   * hero. When provided alongside hasRecording=true, renders an orange primary
   * button above the share/publish row. Typically navigates to the
   * archive-mode player (LiveRoomScreen for Practice Rooms, immersive layout
   * for Live Streams).
   */
  onWatchRecording?: () => void;

  // viewer state
  isSubscribed: boolean;
  isSubscribeLoading: boolean;
  /**
   * CORE-879 — Live-сессия в календаре зрителя по купленной программе, а не по
   * «Я иду». Переключать здесь нечего: строки подписки под ней нет, и нажатие
   * лишь завело бы ручную, после которой сервер перестаёт считать сессию
   * программной. Кнопка RSVP показывает эту подпись и не нажимается.
   */
  rsvpLockedLabel?: string;
  isFavorite: boolean;
  isFavoriteLoading: boolean;

  // owner state
  isHidden: boolean;
  isPublishLoading: boolean;
  isPreviewMode: boolean;
  viewerCount: number;

  // Labels (for i18n passthrough)
  labels: {
    join: string;
    going: string;
    share: string;
    invite: string;
    /** CORE-1435 — подписи кнопки напоминания (нужны вместе с `onRemindToggle`). */
    remind?: string;
    reminded?: string;
    repost?: string;
  };

  // Handlers
  onBack: () => void;
  onSubscribeToggle: () => void;
  /**
   * CORE-879 — без обработчика «Поделиться» и «Пригласить» не рисуются: у
   * закрытой сессии программы ссылка у получателя не откроется
   * (screens/live/liveShareGate).
   */
  onShare?: () => void;
  onFavoriteToggle: () => void;
  onMasterPress: () => void;
  onPreview: () => void;
  onPublish: () => void;
  onEdit: () => void;
  onOpenEditModal: () => void;
  /** SCRUM-49 — Owner-only: opens the action-sheet menu (edit / delete /
   *  cancel series). When provided, the pencil routes here instead of
   *  straight to the edit modal. Falls back to onOpenEditModal when
   *  omitted so older parents that haven't been migrated still work. */
  onOpenOwnerMenu?: () => void;
  /** Owner-only: hard-delete the event with a confirmation prompt and a
   *  cancellation post to the event chat. Wired up by LiveDetailScreen. */
  onDelete?: () => void;
  /** Owner-only, archive-only: open a flow to recreate the past event on
   *  a new date with the same title/cover/description. Replaces the Edit
   *  button in the ended state — per Оксана 2026-05-07, masters don't
   *  edit a finished broadcast, they spawn a new one. */
  onRecreate?: () => void;
  onAuthRequired?: () => void;
  /** Optional handler for the «Чат с ведущим» CTA. When provided and the
   *  viewer is authenticated + not the owner, a chat icon-button renders
   *  next to share. Click should attempt createDmConversation against the
   *  host master and surface MUTUAL_FOLLOW_REQUIRED via toast/alert. */
  onChatWithMaster?: () => void;
  /** Favorites toggle — viewer-side. When `onSaveToggle` is provided, a
   *  heart icon-button renders next to «Пригласить» so the viewer can add
   *  the announcement to their favorites (kind='event_saved'). After the
   *  live ends, the saved entry transitions to the recording. Owner view
   *  doesn't render the button — you don't favorite your own events. */
  isSaved?: boolean;
  onSaveToggle?: () => void;
  /** CORE-26 — жалоба на лайв-сессию. When provided and the viewer is not
   *  the owner, a flag icon-button renders in the CTA row. */
  onReport?: () => void;
  /** CORE-1693 — репост эфира (или записи) в свою ленту. */
  onRepost?: () => void;

  /**
   * When `true`, the hero renders the COMPACT variant used by the
   * 2-column event detail page (LiveDetailScreen DEFAULT layout):
   *   - hides the in-hero title + description block
   *   - hides the master "ВЕДУЩИЙ LIVE-СЕССИИ" chip at the bottom
   *   - drops the inner ScrollView wrapper so the page's outer scroll
   *     can flow past the hero into the meta + comments section
   *
   * Title/description/master live in `LiveMetaSection` below the hero
   * in this variant; comments live in `LiveCommentsPanel` to the right
   * (or stacked, on narrow viewports). Default `false` — legacy
   * full-page callers (e.g. RoomAnnouncementScreen) are unaffected.
   */
  splitOutMeta?: boolean;

  /**
   * AMB-218 — suppress the slide-in chat capsule entirely.
   *
   * RoomAnnouncementScreen mounts this hero for /room/:id but
   * `RoomResponse` doesn't carry the host's master profile id — only the
   * host User UUID. The screen falls back to `parseInt(hostUserId, 10)`,
   * which on a UUID like `882928ca-...` quietly yields `882928`. The
   * chat capsule then tries to watch a non-existent
   * `master-circle-882928` channel, Stream's client keeps the bogus
   * channel watched, and `/channels/.../query` gets hammered on every
   * reconnect — across navigations too, because the leaked channel
   * never gets `stopWatching()`-ed (now fixed in useEventThread).
   *
   * Suppress the panel until BE adds `hostMasterProfileId` to
   * `RoomResponse`; community-hosted rooms (real master id 7) still
   * get the chat via the same path because the hero is mounted with a
   * valid id and disableChat=false in that case.
   */
  disableChat?: boolean;
  /**
   * Optional content rendered below the hero, inside the hero's own scroll
   * (full-page mode). RoomAnnouncementScreen uses it to mount the stable
   * always-visible discussion panel (LiveCommentsPanel) where the legacy
   * slide-in «Начать обсуждение» capsule used to live. Gated by `disableChat`.
   */
  footer?: React.ReactNode;
};

type CountdownParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const computeCountdown = (target: Date): CountdownParts => {
  const diffMs = Math.max(0, target.getTime() - Date.now());
  const days = Math.floor(diffMs / 86_400_000);
  const hours = Math.floor((diffMs % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
  const seconds = Math.floor((diffMs % 60_000) / 1_000);
  return { days, hours, minutes, seconds };
};

const pluralRu = (n: number, one: string, few: string, many: string): string => {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
};

type TimeUnit = { value: number; label: string };

const getCountdownUnits = (c: CountdownParts): TimeUnit[] => {
  if (c.days > 0) {
    return [
      { value: c.days, label: pluralRu(c.days, 'день', 'дня', 'дней') },
      { value: c.hours, label: pluralRu(c.hours, 'час', 'часа', 'часов') },
    ];
  }
  if (c.hours > 0) {
    return [
      { value: c.hours, label: pluralRu(c.hours, 'час', 'часа', 'часов') },
      { value: c.minutes, label: pluralRu(c.minutes, 'минута', 'минуты', 'минут') },
    ];
  }
  if (c.minutes > 0) {
    return [
      { value: c.minutes, label: pluralRu(c.minutes, 'минута', 'минуты', 'минут') },
      { value: c.seconds, label: pluralRu(c.seconds, 'секунда', 'секунды', 'секунд') },
    ];
  }
  return [
    { value: c.seconds, label: pluralRu(c.seconds, 'секунда', 'секунды', 'секунд') },
  ];
};

const formatWhen = (date: Date): string => {
  const weekday = date.toLocaleDateString('ru-RU', { weekday: 'long' });
  const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const day = date.getDate();
  const month = date.toLocaleDateString('ru-RU', { month: 'short' });
  return `${capitalized}, ${day} ${month}, ${hh}:${mm}`;
};

// CORE-2004 — bundled landscape; the third-party photo host it replaced times
// out from Russia, so the "fallback" left the hero black there.
const FALLBACK_BG = require('../../assets/home/approved-path-landscape.webp');

/**
 * CORE-744 — module scope on purpose, and it must stay here.
 *
 * This used to be declared inside `LiveAnnouncementHero`, which gave it a new
 * function identity on every render. React compares element types by identity,
 * so a fresh identity reads as «a different component»: the entire hero was
 * unmounted and rebuilt from scratch instead of being updated in place.
 *
 * The announcement screen re-renders once a second (the countdown tick), so on
 * the waiting screen that meant a full teardown twice a second — which reset
 * the ScrollView's scroll offset to 0 under the reader (measured: a 400px jump
 * with no input) and remounted the comments panel, re-opening its chat
 * connection ~2×/s. Compact mode renders no wrapper at all, so the fragment
 * branch stays as it was.
 */
const HeroOuter: React.FC<{ bare: boolean; children: React.ReactNode }> = ({ bare, children }) =>
  bare ? (
    <>{children}</>
  ) : (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ flexGrow: 1 }}
      showsVerticalScrollIndicator={false}
    >
      {children}
    </ScrollView>
  );

export const LiveAnnouncementHero: React.FC<LiveAnnouncementHeroProps> = ({
  liveId,
  eventKind = 'livestream',
  title,
  description,
  startDateTimeUtc,
  durationMinutes,
  master,
  bgImageUri,
  masterAvatarUrl,
  canJoinNow = false,
  onJoinNow,
  onJoinBeforeStart,
  onRemindToggle,
  isAwaitingHost = false,
  isEnded,
  isLive,
  isOwner,
  canEdit = true,
  canHost = false,
  isAuthenticated,
  endedVariant = 'completed',
  hostNoShow = false,
  hasRecording = false,
  recordingOutcome,
  onWatchRecording,
  access,
  onOpenAgapellaPaywall,
  isSubscribed,
  isSubscribeLoading,
  rsvpLockedLabel,
  isFavorite,
  isFavoriteLoading,
  isHidden,
  isPublishLoading,
  isPreviewMode,
  viewerCount,
  labels,
  onBack,
  onSubscribeToggle,
  onShare,
  onRepost,
  onFavoriteToggle,
  isSaved,
  onSaveToggle,
  onReport,
  onRecreate,
  onMasterPress,
  onPreview,
  onPublish,
  onEdit,
  onOpenOwnerMenu,
  onOpenEditModal,
  onDelete,
  onAuthRequired,
  onChatWithMaster,
  splitOutMeta = false,
  disableChat = false,
  footer,
}) => {
  const { width, height } = useWindowDimensions();
  const isDesktop = width >= 900;
  const headerOffset = useReservedZoneHeight(HEADER_ZONE_ID, HEADER_HEIGHT_FALLBACK);
  // CORE-1435 — баннер cookie лежит поверх низа страницы и на телефоне закрывал
  // кнопки входа. Полностраничный герой (анонс /room) добавляет под них столько
  // же места, сколько занимает баннер; 0 — баннера нет, раскладка прежняя.
  // Компактный герой LiveDetail внизу страницы не стоит — ему запас не нужен.
  const cookieBannerInset = useCookieBannerInset();
  const bottomInset = splitOutMeta ? 0 : cookieBannerInset;
  const isWide = width >= 1280;

  const target = new Date(startDateTimeUtc);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (isEnded || isLive) return;
    const t = setInterval(() => setTick((n) => n + 1), 1_000);
    return () => clearInterval(t);
  }, [isEnded, isLive]);

  const countdown = computeCountdown(target);
  const units = getCountdownUnits(countdown);

  const statusPillText = isEnded
    ? 'ЗАВЕРШЕНО'
    : isLive || (eventKind === 'live_room' && canJoinNow)
    ? eventKind === 'live_room'
      ? 'LIVE-СЕССИЯ ОТКРЫТА'
      : eventKind === 'offline_event'
      ? 'СЕЙЧАС ИДЁТ'
      : 'LIVE'
    : eventKind === 'live_room'
    ? 'LIVE-СЕССИЯ'
    : eventKind === 'offline_event'
    ? 'ВСТРЕЧА'
    : 'LIVE-СЕССИЯ';
  const statusPillDotColor = isEnded
    ? '#9E9E9E'
    : isLive || (eventKind === 'live_room' && canJoinNow)
    ? '#EF4444'
    : '#E98245';

  // Compact (splitOutMeta) mode renders a card-like hero, not a full-page
  // takeover — the 2-column page below needs vertical room to breathe.
  // Legacy callers (RoomAnnouncementScreen) still get the full-page hero.
  const heroMinHeight = splitOutMeta
    ? (isDesktop ? 520 : 460)
    : (isDesktop ? Math.max(780, height) : Math.max(760, height));

  // Master avatar can be a URL string OR a local asset (e.g. community logo
   // via require()). Normalize to a single Image `source` prop.
  const avatarSource: ImageSourcePropType | undefined =
    typeof masterAvatarUrl === 'string' && masterAvatarUrl
      ? { uri: masterAvatarUrl }
      : masterAvatarUrl && typeof masterAvatarUrl !== 'string'
      ? masterAvatarUrl
      : undefined;

  const [bgFailed, setBgFailed] = useState(false);
  const bgSource = bgFailed || !bgImageUri ? FALLBACK_BG : { uri: bgImageUri };

  // In compact mode the parent screen owns the outer scroll (so the
  // 2-column section under the hero can flow into view). Legacy
  // full-page callers keep the inner ScrollView so the hero behaves as
  // a self-contained scrolling panel.
  return (
    <HeroOuter bare={splitOutMeta}>
      <ImageBackground
        source={bgSource}
        style={[styles.hero, { minHeight: heroMinHeight, backgroundColor: '#1A1814' }]}
        imageStyle={styles.heroImage}
        resizeMode="cover"
        onError={() => setBgFailed(true)}
      >
        {Platform.OS === 'web' && (
          <>
            {/* Ambient painted fallback — always present under the photo.
                CORE-1171: «под» надо задать явно. react-native-web кладёт саму
                картинку ImageBackground слоем zIndex -1, а этот div шёл после
                неё с auto — и непрозрачный градиент закрывал фото целиком.
                На вебе обложка сессии не была видна на анонсе вовсе, и
                сменённую в правке обложку мастер считал незагрузившейся. */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: -2,
                background:
                  'radial-gradient(120% 80% at 20% 10%, rgba(92,64,140,0.55) 0%, rgba(20,22,38,0.0) 55%), radial-gradient(90% 70% at 90% 90%, rgba(233, 130, 69,0.35) 0%, rgba(20,22,38,0.0) 60%), linear-gradient(180deg, #0F1420 0%, #0A0C14 100%)',
                pointerEvents: 'none',
              } as any}
            />
            {/* Tonal overlay on top of the photo for text legibility */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(8,14,22,0.45) 0%, rgba(8,14,22,0.30) 28%, rgba(8,14,22,0.60) 72%, rgba(8,14,22,0.92) 100%)',
                pointerEvents: 'none',
              } as any}
            />
          </>
        )}

        <View
          style={[
            styles.content,
            {
              paddingTop: headerOffset + (isDesktop ? 28 : 20),
              paddingHorizontal: isDesktop ? 40 : 20,
              paddingBottom: (isDesktop ? 56 : 36) + bottomInset,
              maxWidth: isWide ? 1280 : 1080,
            },
          ]}
        >
          {/* Top bar */}
          <View style={styles.topBar}>
            <TouchableOpacity
              onPress={onBack}
              style={styles.backBtn}
              accessibilityLabel="Назад"
              activeOpacity={0.85}
            >
              <Ionicons name="chevron-back" size={18} color="#0D0D0D" />
              <Typography style={styles.backBtnText}>НАЗАД</Typography>
            </TouchableOpacity>

            <View style={[styles.overlinePill, isEnded && styles.overlinePillMuted]}>
              <View
                style={[
                  styles.overlineDot,
                  { backgroundColor: statusPillDotColor },
                ]}
              />
              <Typography style={styles.overlineText}>{statusPillText}</Typography>
            </View>
          </View>

          {/* Title + datetime — skipped in compact (splitOutMeta) mode
              because LiveMetaSection below the hero owns this content on
              the 2-column event detail page. */}
          {splitOutMeta ? null : (
          <View style={[styles.titleBlock, { marginTop: isDesktop ? 56 : 36 }]}>
            <Typography style={styles.dateLine}>{formatWhen(target)}</Typography>
            <View
              style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'center', marginTop: 12 }}
            >
              {/* CORE-1399 — на вебе Typography отбрасывает fontSize/fontWeight из
                  style, начертание задаёт только variant. Без него название и
                  описание обе падали в body и читались как повтор. */}
              <Typography
                variant="h3"
                style={[styles.headline, isDesktop && styles.headlineDesktop]}
              >
                {title}
              </Typography>
              {isOwner && canEdit && (
                // SCRUM-49: pencil routes through onOpenOwnerMenu (action sheet
                // → edit/delete/cancel-series) when the parent has wired it.
                // Falls back to onOpenEditModal for legacy parents. The
                // isEnded gate is gone — owners can edit/cancel before start too.
                // AMB-152: hidden when canEdit=false (no edit feature, e.g.
                // scheduled rooms) so the pencil isn't a dead no-op.
                <TouchableOpacity
                  onPress={onOpenOwnerMenu ?? onOpenEditModal}
                  style={styles.titleEditBtn}
                  activeOpacity={0.85}
                >
                  <Ionicons name="pencil" size={16} color="rgba(255,255,255,0.85)" />
                </TouchableOpacity>
              )}
            </View>
            {description ? (
              <Typography variant="body" style={[styles.tagline, { marginTop: isDesktop ? 20 : 14 }]}>
                {description}
              </Typography>
            ) : isOwner && isEnded && canEdit ? (
              <TouchableOpacity
                onPress={onOpenEditModal}
                style={{ marginTop: isDesktop ? 20 : 14, flexDirection: 'row', alignItems: 'center', alignSelf: 'center' }}
                activeOpacity={0.85}
              >
                <Ionicons name="add-circle-outline" size={18} color="rgba(255,255,255,0.7)" />
                <Typography style={[styles.tagline, { marginLeft: 6 }]}>Добавить описание</Typography>
              </TouchableOpacity>
            ) : null}
          </View>
          )}

          {/* Middle — countdown or status message */}
          <View style={[styles.middleBlock, { paddingVertical: isDesktop ? 56 : 32 }]}>
            <AgapellaBadge access={access} style={styles.agapellaBadge} testID="live-hero-agapella-badge" />
            {isEnded ? (() => {
              // AMB-48 — live rooms (library practices) and live streams share
              // this hero, but the user-facing language must reflect the kind
              // of event. «Эфир» reads wrong on a library practice and the
              // viewer-count label («Зрителей») doesn't match the «participants»
              // mental model a room has.
              const isRoom = eventKind === 'live_room';
              const recordingOfEvent = isRoom ? 'Запись практики' : 'Запись Live-сессии';
              const viewerLabel = isRoom ? 'Участников' : 'Зрителей';
              // CORE-981 — resolve title/body via the shared helper so the
              // host_no_show branch is checked BEFORE the generic owner copy.
              const endedCopy = resolveEndedHeroCopy({
                isOwner,
                isRoom,
                hasRecording,
                hostNoShow,
                endedVariant,
                masterName: master.name,
                recordingOutcome,
              });
              return (
              <View style={styles.endedBlock}>
                <TouchableOpacity
                  style={styles.endedIcon}
                  activeOpacity={hasRecording && onWatchRecording ? 0.75 : 1}
                  onPress={hasRecording && onWatchRecording ? onWatchRecording : undefined}
                  disabled={!hasRecording || !onWatchRecording}
                >
                  <Ionicons
                    name={
                      hasRecording
                        ? 'play-circle'
                        : endedVariant === 'subscribers-only' || endedVariant === 'attendees-only' || endedVariant === 'agapella'
                        ? 'lock-closed-outline'
                        : 'videocam-off-outline'
                    }
                    size={hasRecording ? 44 : 32}
                    color={hasRecording ? '#E98245' : 'rgba(255,255,255,0.55)'}
                  />
                </TouchableOpacity>
                <Typography
                  style={[styles.endedTitle, isDesktop && styles.endedTitleDesktop]}
                >
                  {endedCopy.title}
                </Typography>
                <Typography style={styles.endedBody}>
                  {endedCopy.body}
                </Typography>
                {isOwner && viewerCount > 0 && (
                  <View style={styles.viewerPill}>
                    <Ionicons name="people" size={14} color="rgba(255,255,255,0.75)" />
                    <Typography style={styles.viewerPillText}>{viewerLabel}: {viewerCount}</Typography>
                  </View>
                )}
              </View>
              );
            })() : isLive ? (
              <View style={styles.liveBlock}>
                <Typography style={styles.countdownLabel}>СЕЙЧАС</Typography>
                <Typography
                  style={[styles.countdownTime, isDesktop && styles.countdownTimeDesktop]}
                >
                  LIVE
                </Typography>
              </View>
            ) : isAwaitingHost ? (
              // CORE-1435 — время вышло, а ведущий ещё не начал: вместо отсчёта
              // «через 0 секунд» (и прежнего ложного «Завершено») — спокойное
              // «вот-вот начнётся». Кнопка входа при этом открыта.
              <View style={styles.liveBlock}>
                <Typography style={styles.countdownLabel}>СЕССИЯ ВОТ-ВОТ НАЧНЁТСЯ</Typography>
                <Typography variant="body" style={styles.tagline}>
                  {isOwner || canHost
                    ? 'Время начала наступило. Войдите и начните сессию.'
                    : 'Ведущий скоро начнёт. Можно войти и подождать внутри.'}
                </Typography>
              </View>
            ) : (
              <>
                <View testID="live-detail-countdown">
                <Typography style={styles.countdownLabel}>НАЧНЁТСЯ ЧЕРЕЗ</Typography>
                <View style={styles.countdownUnits}>
                  {units.map((u, i) => (
                    <React.Fragment key={u.label}>
                      {i > 0 && (
                        <View
                          style={[
                            styles.countdownDivider,
                            isDesktop && { height: 80 },
                          ]}
                        />
                      )}
                      <View style={styles.countdownUnit}>
                        <Typography
                          style={[
                            styles.countdownValue,
                            isDesktop && styles.countdownValueDesktop,
                          ]}
                        >
                          {u.value}
                        </Typography>
                        <Typography style={styles.countdownUnitLabel}>{u.label}</Typography>
                      </View>
                    </React.Fragment>
                  ))}
                </View>
                </View>
              </>
            )}
          </View>

          {/* Bottom — actions + master chip */}
          <View style={[styles.bottomBlock, { gap: isDesktop ? 24 : 18 }]}>
            {(isOwner || canHost) ? (
              <View style={styles.ownerActions}>
                {!isPreviewMode && !isEnded && (() => {
                  // For practice rooms the host is just entering early — say
                  // «Войти в комнату» instead of «Предпросмотр». When the
                  // event is already live (status=Active or countdown=0),
                  // promote the CTA to a stronger «Открыть эфир» so the
                  // host has a clear primary action.
                  const isRoom = eventKind === 'live_room';
                  const cta = isLive
                    ? (isRoom ? 'Открыть Live-сессию' : 'Открыть Live-сессию')
                    : (isRoom ? 'Войти в Live-сессию' : 'Предпросмотр Live-сессии');
                  return (
                    <TouchableOpacity
                      testID="live-detail-host-cta"
                      activeOpacity={0.85}
                      onPress={onPreview}
                      style={isLive ? styles.btnPrimaryLive : styles.btnPrimaryLight}
                    >
                      <Ionicons
                        name={isLive ? 'radio' : 'videocam'}
                        size={18}
                        color={isLive ? ON_ACCENT_TEXT : '#0D0D0D'}
                        style={{ marginRight: 8 }}
                      />
                      <Typography style={isLive ? styles.btnPrimaryLiveText : styles.btnPrimaryLightText}>
                        {cta}
                      </Typography>
                    </TouchableOpacity>
                  );
                })()}

                {/* Post-broadcast «Смотреть запись» — owner-only primary CTA
                    once the recording is published. Mirrors the Twitch
                    «Watch VOD» action surfaced on the post-stream screen. */}
                {isEnded && hasRecording && onWatchRecording && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onWatchRecording}
                    style={styles.btnPrimaryLive}
                  >
                    <Ionicons name="play" size={18} color={ON_ACCENT_TEXT} style={{ marginRight: 8 }} />
                    <Typography style={styles.btnPrimaryLiveText}>Смотреть запись</Typography>
                  </TouchableOpacity>
                )}

                {/* CORE-1149 — публикует событие владелец профиля; назначенный
                    ведущий его только проводит. */}
                {isOwner && (
                <TouchableOpacity
                  testID="live-detail-publish"
                  activeOpacity={0.85}
                  onPress={onPublish}
                  disabled={!isHidden || isPublishLoading}
                  style={[
                    styles.btnOutlineLg,
                    isHidden && { backgroundColor: '#22C55E', borderColor: '#22C55E' },
                  ]}
                >
                  {isPublishLoading ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <>
                      <Ionicons
                        name="checkmark-circle-outline"
                        size={18}
                        color="#FFFFFF"
                        style={{ marginRight: 8 }}
                      />
                      <Typography style={styles.btnOutlineLgText}>
                        {isHidden ? 'Опубликовать' : 'Опубликовано'}
                      </Typography>
                    </>
                  )}
                </TouchableOpacity>
                )}

                {/* Edit only on the upcoming announcement — once the stream
                    has started or finished, the relevant action is
                    «Завершить» (live) or «Пересоздать на новую дату»
                    (archive), per Оксана 2026-05-07. CORE-1171 — подписанной
                    кнопкой, а не значком рядом с «Поделиться»: значок на
                    проде не находили и считали, что правки нет вовсе. */}
                {isOwner && !isLive && !isEnded && canEdit && (
                  <TouchableOpacity
                    testID="live-detail-edit"
                    activeOpacity={0.85}
                    onPress={onEdit}
                    style={styles.btnOutlineLg}
                    accessibilityRole="button"
                  >
                    <Ionicons
                      name="create-outline"
                      size={18}
                      color="#FFFFFF"
                      style={{ marginRight: 8 }}
                    />
                    <Typography style={styles.btnOutlineLgText}>Редактировать</Typography>
                  </TouchableOpacity>
                )}

                <View style={styles.ctaRow}>
                  {onShare && (
                    <TouchableOpacity
                      activeOpacity={0.85}
                      onPress={onShare}
                      style={[styles.btnPrimary, { flex: 1 }]}
                    >
                      <Ionicons
                        name="share-social-outline"
                        size={18}
                        color={ON_ACCENT_TEXT}
                        style={{ marginRight: 8 }}
                      />
                      <Typography style={styles.btnPrimaryText}>{labels.share}</Typography>
                    </TouchableOpacity>
                  )}

                  {/* Archive recreate — surfaces only on a finished
                      broadcast. Spawns a new live event with the same
                      title/cover/description on a date the owner picks. */}
                  {isEnded && onRecreate && (
                    <TouchableOpacity
                      activeOpacity={0.85}
                      onPress={onRecreate}
                      style={styles.btnIconOutline}
                      accessibilityLabel="Пересоздать на новую дату"
                    >
                      <Ionicons name="refresh-outline" size={20} color="#FFFFFF" />
                    </TouchableOpacity>
                  )}
                  {onDelete && !isEnded && (
                    <TouchableOpacity
                      testID="live-detail-delete"
                      activeOpacity={0.85}
                      onPress={onDelete}
                      style={[styles.btnIconOutline, { borderColor: 'rgba(239, 68, 68, 0.55)' } as any]}
                      accessibilityRole="button"
                      accessibilityLabel="Удалить событие"
                    >
                      <Ionicons name="trash-outline" size={20} color="#FCA5A5" accessible={false} />
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            ) : isEnded ? (
              <View style={styles.ctaRow}>
                {/* Non-owner viewer with a published recording — primary
                    «Смотреть запись» action. Equivalent to YouTube «Watch
                    replay» on a finished live. */}
                {hasRecording && onWatchRecording && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onWatchRecording}
                    style={[styles.btnPrimary, { flex: 1 }]}
                  >
                    <Ionicons name="play" size={18} color={ON_ACCENT_TEXT} style={{ marginRight: 8 }} />
                    <Typography style={styles.btnPrimaryText}>Смотреть запись</Typography>
                  </TouchableOpacity>
                )}

                {!hasRecording && endedVariant === 'agapella' && onOpenAgapellaPaywall && (
                  <TouchableOpacity
                    testID="live-hero-agapella-open"
                    activeOpacity={0.85}
                    onPress={onOpenAgapellaPaywall}
                    style={[styles.btnPrimary, { flex: 1 }]}
                  >
                    <Typography style={styles.btnPrimaryText}>Открыть доступ</Typography>
                  </TouchableOpacity>
                )}

                {!hasRecording && (endedVariant === 'subscribers-only' || endedVariant === 'attendees-only') && !isSubscribed && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onSubscribeToggle}
                    disabled={isSubscribeLoading}
                    style={[styles.btnPrimary, { flex: 1 }]}
                  >
                    {isSubscribeLoading ? (
                      <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
                    ) : (
                      <Typography style={styles.btnPrimaryText}>ПОДПИСАТЬСЯ</Typography>
                    )}
                  </TouchableOpacity>
                )}

                {onShare && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onShare}
                    style={[styles.btnOutline, { flex: 1 }]}
                  >
                    <Typography style={styles.btnOutlineText}>{labels.share}</Typography>
                  </TouchableOpacity>
                )}
                {onRepost && (
                  <TouchableOpacity
                    testID="live-hero-repost"
                    activeOpacity={0.85}
                    onPress={onRepost}
                    style={styles.btnIconOutline}
                    accessibilityLabel={labels.repost ?? 'Репост'}
                  >
                    <Ionicons name="repeat-outline" size={20} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
                {onReport && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onReport}
                    style={styles.btnIconOutline}
                    accessibilityLabel="Пожаловаться"
                  >
                    <Ionicons name="flag-outline" size={20} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            ) : canJoinNow && onJoinNow ? (
              <View style={styles.ctaRow}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  onPress={onJoinNow}
                  style={[styles.btnPrimary, styles.btnPrimaryJoin]}
                >
                  <Ionicons name="play" size={18} color={ON_ACCENT_TEXT} style={{ marginRight: 8 }} />
                  <Typography style={styles.btnPrimaryText}>ВОЙТИ В LIVE-СЕССИЮ</Typography>
                </TouchableOpacity>

                {onShare && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onShare}
                    style={styles.btnOutline}
                  >
                    <Typography style={styles.btnOutlineText}>{labels.invite}</Typography>
                  </TouchableOpacity>
                )}
                {onReport && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onReport}
                    style={styles.btnIconOutline}
                    accessibilityLabel="Пожаловаться"
                  >
                    <Ionicons name="flag-outline" size={20} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            ) : (
              <View style={styles.ctaRow}>
                {onJoinBeforeStart && onRemindToggle ? (
                  <>
                    {/* CORE-1435 — вход не зависит от напоминания и никогда не
                        выглядит выключенным: двойной тап гасит сам экран. */}
                    <TouchableOpacity
                      testID="room-announcement-join"
                      accessibilityRole="button"
                      activeOpacity={0.85}
                      onPress={onJoinBeforeStart}
                      style={[styles.btnPrimary, styles.btnGrow]}
                    >
                      <Typography style={styles.btnPrimaryText}>{labels.join}</Typography>
                    </TouchableOpacity>
                    {/* Напоминание — контурная кнопка: включённое состояние
                        читается по оранжевой рамке и галочке, а не по белой
                        заливке, которая выглядела как неактивная. Спиннер — только
                        пока идёт запрос. */}
                    <TouchableOpacity
                      testID="room-announcement-remind"
                      accessibilityRole="button"
                      {...({ 'aria-pressed': isSubscribed, 'aria-busy': isSubscribeLoading } as any)}
                      activeOpacity={0.85}
                      onPress={onRemindToggle}
                      disabled={isSubscribeLoading}
                      style={[styles.btnOutline, styles.btnRemind, styles.btnGrow, isSubscribed && styles.btnRemindOn]}
                    >
                      {isSubscribeLoading ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <>
                          <Ionicons
                            name={isSubscribed ? 'checkmark' : 'notifications-outline'}
                            size={16}
                            color={isSubscribed ? '#E98245' : '#FFFFFF'}
                            style={{ marginRight: 8 }}
                          />
                          <Typography style={styles.btnOutlineText}>
                            {isSubscribed ? labels.reminded : labels.remind}
                          </Typography>
                        </>
                      )}
                    </TouchableOpacity>
                  </>
                ) : rsvpLockedLabel ? (
                  // CORE-879 — сессия купленной программы: состояние, а не кнопка.
                  <View
                    testID="live-detail-rsvp-locked"
                    accessibilityRole="text"
                    style={[styles.btnPrimary, styles.btnPrimaryGoing]}
                  >
                    <Ionicons name="checkmark" size={16} color="#0D0D0D" style={{ marginRight: 6 }} />
                    <Typography style={[styles.btnPrimaryText, { color: '#0D0D0D' }]}>
                      {rsvpLockedLabel}
                    </Typography>
                  </View>
                ) : (
                <TouchableOpacity
                  activeOpacity={0.85}
                  onPress={onSubscribeToggle}
                  disabled={isSubscribeLoading}
                  style={[
                    styles.btnPrimary,
                    isSubscribed && styles.btnPrimaryGoing,
                  ]}
                >
                  {isSubscribeLoading ? (
                    <ActivityIndicator size="small" color={isSubscribed ? '#0D0D0D' : ON_ACCENT_TEXT} />
                  ) : (
                    <>
                      {isSubscribed && (
                        <Ionicons
                          name="checkmark"
                          size={16}
                          color="#0D0D0D"
                          style={{ marginRight: 6 }}
                        />
                      )}
                      <Typography
                        style={[
                          styles.btnPrimaryText,
                          isSubscribed && { color: '#0D0D0D' },
                        ]}
                      >
                        {isSubscribed ? labels.going : labels.join}
                      </Typography>
                    </>
                  )}
                </TouchableOpacity>
                )}

                {onShare && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onShare}
                    style={styles.btnOutline}
                  >
                    <Typography style={styles.btnOutlineText}>{labels.invite}</Typography>
                  </TouchableOpacity>
                )}
                {/* Chat-with-host shortcut. The handler attempts to start
                    a 1:1 DM and surfaces MUTUAL_FOLLOW_REQUIRED inline,
                    so we don't pre-fetch canMessage just to gate the icon. */}
                {isAuthenticated && onChatWithMaster && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onChatWithMaster}
                    style={styles.btnIconOutline}
                    accessibilityLabel="Чат с ведущим"
                  >
                    <Ionicons name="chatbubble-ellipses-outline" size={20} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
                {/* Bookmark toggle — saves the announcement to the user's
                    feed under kind='event_saved'. Guests get nudged to
                    auth via onAuthRequired. */}
                {onSaveToggle && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={() => {
                      if (!isAuthenticated && onAuthRequired) { onAuthRequired(); return; }
                      onSaveToggle();
                    }}
                    style={styles.btnIconOutline}
                    accessibilityLabel={favoriteA11yLabel(isSaved)}
                  >
                    {/* CORE-603 — the saved state used to be invisible here:
                        the icon stayed white either way. */}
                    <Ionicons
                      name={favoriteIconName(isSaved)}
                      size={20}
                      color={isSaved ? '#E98245' : '#FFFFFF'}
                    />
                  </TouchableOpacity>
                )}
                {onReport && (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={onReport}
                    style={styles.btnIconOutline}
                    accessibilityLabel="Пожаловаться"
                  >
                    <Ionicons name="flag-outline" size={20} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            )}

            {/* Master "ВЕДУЩИЙ LIVE-СЕССИИ" chip — skipped in compact mode
                because LiveMetaSection below the hero owns the presenter
                row (with the Follow CTA). */}
            {splitOutMeta ? null : (
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={onMasterPress}
              style={styles.masterChip}
            >
              <View style={styles.masterAvatar}>
                {avatarSource ? (
                  <Image
                    source={avatarSource}
                    style={styles.masterAvatarImage as ImageStyle}
                    resizeMode="cover"
                  />
                ) : (
                  <Ionicons name="person" size={22} color="#FFFFFF" />
                )}
              </View>
              <View style={styles.masterChipTextBlock}>
                <Typography style={styles.masterChipEyebrow}>ВЕДУЩИЙ LIVE-СЕССИИ</Typography>
                <Typography style={styles.masterChipTitle}>{master.name}</Typography>
              </View>
              <View style={styles.masterChipArrow}>
                <Ionicons name="arrow-forward" size={16} color="#FFFFFF" />
              </View>
            </TouchableOpacity>
            )}
          </View>
        </View>
      </ImageBackground>
      {!disableChat && footer}
    </HeroOuter>
  );
};

const styles = StyleSheet.create({
  hero: {
    width: '100%',
    position: 'relative',
  } as ViewStyle,
  heroImage: {
    width: '100%',
    height: '100%',
  } as ImageStyle,
  content: {
    flex: 1,
    width: '100%',
    alignSelf: 'center',
    zIndex: 1,
  } as ViewStyle,
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as ViewStyle,
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 100,
    backgroundColor: 'rgba(255,255,255,0.92)',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(10px)',
      cursor: 'pointer',
      boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
    } as any),
  } as any,
  backBtnText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: '#0D0D0D',
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    lineHeight: 14,
    marginLeft: 2,
  } as TextStyle,
  overlinePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 100,
    backgroundColor: 'rgba(255,255,255,0.92)',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(10px)',
      boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
    } as any),
  } as any,
  overlinePillMuted: {
    backgroundColor: 'rgba(255,255,255,0.82)',
  } as ViewStyle,
  overlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  } as ViewStyle,
  overlineText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: '#0D0D0D',
    letterSpacing: 2,
    textTransform: 'uppercase',
    lineHeight: 14,
  } as TextStyle,
  titleBlock: {
    alignItems: 'center',
  } as ViewStyle,
  dateLine: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: fonts.display,
    color: 'rgba(255,255,255,0.78)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    lineHeight: 16,
    ...(Platform.OS === 'web' && {
      textShadow: '0 1px 12px rgba(0,0,0,0.35)',
    } as any),
  } as any,
  headline: {
    ...typography.h1Hero,
    fontSize: Platform.OS === 'web' ? 52 : 34,
    lineHeight: Platform.OS === 'web' ? 60 : 40,
    color: '#FFFFFF',
    textAlign: 'center',
    ...(Platform.OS === 'web' && {
      textShadow: '0 2px 24px rgba(0,0,0,0.45)',
    } as any),
  } as any,
  headlineDesktop: {
    fontSize: 76,
    lineHeight: 84,
    letterSpacing: -2.4,
  } as TextStyle,
  titleEditBtn: {
    marginLeft: 12,
    marginTop: 6,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  tagline: {
    fontSize: Platform.OS === 'web' ? 17 : 14,
    lineHeight: Platform.OS === 'web' ? 26 : 20,
    fontWeight: '500',
    fontFamily: fonts.text,
    color: 'rgba(255,255,255,0.86)',
    textAlign: 'center',
    maxWidth: 620,
    alignSelf: 'center',
    ...(Platform.OS === 'web' && {
      textShadow: '0 1px 12px rgba(0,0,0,0.35)',
    } as any),
  } as any,
  agapellaBadge: { alignSelf: 'center', marginBottom: 16 },
  middleBlock: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  countdownLabel: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: 'rgba(255,255,255,0.85)',
    letterSpacing: 2.5,
    textTransform: 'uppercase',
    lineHeight: 16,
    marginBottom: 24,
  } as TextStyle,
  countdownUnits: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
  } as ViewStyle,
  countdownUnit: {
    alignItems: 'center',
    minWidth: 88,
  } as ViewStyle,
  countdownValue: {
    fontSize: 72,
    lineHeight: 76,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: '#FFFFFF',
    letterSpacing: -2,
    ...(Platform.OS === 'web' && {
      textShadow: '0 4px 32px rgba(0,0,0,0.35)',
      fontVariantNumeric: 'tabular-nums',
    } as any),
  } as any,
  countdownValueDesktop: {
    fontSize: 112,
    lineHeight: 116,
    letterSpacing: -4,
  } as TextStyle,
  countdownUnitLabel: {
    marginTop: 8,
    fontSize: 11,
    fontWeight: '600',
    fontFamily: fonts.display,
    color: 'rgba(255,255,255,0.75)',
    letterSpacing: 2,
    textTransform: 'uppercase',
    lineHeight: 14,
  } as TextStyle,
  countdownDivider: {
    width: 1,
    height: 52,
    backgroundColor: 'rgba(255,255,255,0.18)',
    marginTop: 8,
  } as ViewStyle,
  countdownTime: {
    fontSize: 56,
    lineHeight: 64,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: '#FFFFFF',
    letterSpacing: -1,
    ...(Platform.OS === 'web' && {
      textShadow: '0 4px 32px rgba(0,0,0,0.35)',
    } as any),
  } as any,
  countdownTimeDesktop: {
    fontSize: 80,
    lineHeight: 88,
    letterSpacing: -2,
  } as TextStyle,
  liveBlock: {
    alignItems: 'center',
  } as ViewStyle,
  endedBlock: {
    alignItems: 'center',
    maxWidth: 560,
  } as ViewStyle,
  endedIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  } as ViewStyle,
  endedTitle: {
    fontSize: 28,
    lineHeight: 34,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: '#FFFFFF',
    letterSpacing: -0.5,
    textAlign: 'center',
    ...(Platform.OS === 'web' && {
      textShadow: '0 2px 20px rgba(0,0,0,0.45)',
    } as any),
  } as any,
  endedTitleDesktop: {
    fontSize: 40,
    lineHeight: 46,
    letterSpacing: -1,
  } as TextStyle,
  endedBody: {
    marginTop: 14,
    fontSize: 15,
    lineHeight: 22,
    fontWeight: '500',
    fontFamily: fonts.text,
    color: 'rgba(255,255,255,0.82)',
    textAlign: 'center',
    ...(Platform.OS === 'web' && {
      textShadow: '0 1px 12px rgba(0,0,0,0.35)',
    } as any),
  } as any,
  viewerPill: {
    marginTop: 18,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 100,
    backgroundColor: 'rgba(255,255,255,0.12)',
  } as ViewStyle,
  viewerPillText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: fonts.text,
    color: 'rgba(255,255,255,0.82)',
  } as TextStyle,
  bottomBlock: {
    alignItems: 'center',
  } as ViewStyle,
  ownerActions: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    gap: 12,
  } as ViewStyle,
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
  } as ViewStyle,
  btnPrimary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E98245',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 100,
    minWidth: 180,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      boxShadow: '0 10px 30px rgba(233, 130, 69,0.28)',
      transition: 'transform 0.2s ease',
    } as any),
  } as any,
  btnPrimaryGoing: {
    backgroundColor: '#FFFFFF',
  } as ViewStyle,
  btnPrimaryJoin: {
    backgroundColor: '#22C55E',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 10px 30px rgba(34,197,94,0.35)',
    } as any),
  } as any,
  btnPrimaryText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    // CORE-1733 (возврат QA 23.10) — «Присоединиться» белым на #E98245 давал
    // 2.71:1; на вебе заливка ещё и переходит в CTA-градиент. Тёмный токен
    // держит 4.9+ на всех стопах; на зелёном «Войти в Live-сессию» — 7.4.
    color: ON_ACCENT_TEXT,
    letterSpacing: 1.2,
    lineHeight: 18,
  } as TextStyle,
  // CORE-1435 — «Присоединиться» и «Напомнить о начале» делят строку на широком
  // экране и встают друг под другом во всю ширину на телефоне.
  btnGrow: {
    flexGrow: 1,
  } as ViewStyle,
  btnRemind: {
    flexDirection: 'row',
  } as ViewStyle,
  btnRemindOn: {
    borderColor: '#E98245',
  } as ViewStyle,
  btnPrimaryLight: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.95)',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 100,
    width: '100%',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      boxShadow: '0 10px 30px rgba(0,0,0,0.28)',
    } as any),
  } as any,
  btnPrimaryLightText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: '#0D0D0D',
    letterSpacing: 0.8,
    lineHeight: 18,
  } as TextStyle,
  // «В эфире сейчас» variant of the primary CTA — solar accent,
  // unmistakable signal that the room is live and the host should jump in.
  btnPrimaryLive: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E98245',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 100,
    width: '100%',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      boxShadow: '0 12px 36px rgba(233, 130, 69,0.45)',
    } as any),
  } as any,
  btnPrimaryLiveText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: ON_ACCENT_TEXT, // CORE-1733 — та же оранжевая заливка, что у btnPrimary
    letterSpacing: 1,
    lineHeight: 18,
  } as TextStyle,
  btnOutlineLg: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 100,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.45)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    width: '100%',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      backdropFilter: 'blur(8px)',
    } as any),
  } as any,
  btnOutlineLgText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: '#FFFFFF',
    letterSpacing: 0.8,
    lineHeight: 18,
  } as TextStyle,
  btnOutline: {
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 100,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.85)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 180,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      backdropFilter: 'blur(8px)',
    } as any),
  } as any,
  btnOutlineText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: fonts.display,
    color: '#FFFFFF',
    letterSpacing: 0.5,
    lineHeight: 18,
  } as TextStyle,
  btnIconOutline: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.45)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    } as any),
  } as any,
  favoriteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 22,
    paddingVertical: 10,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      backdropFilter: 'blur(8px)',
    } as any),
  } as any,
  favoriteBtnText: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: fonts.display,
    color: 'rgba(255,255,255,0.85)',
    letterSpacing: 0.3,
    lineHeight: 18,
  } as TextStyle,
  masterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 8,
    paddingRight: 8,
    paddingVertical: 8,
    borderRadius: 100,
    backgroundColor: 'rgba(0,0,0,0.45)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    gap: 12,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      backdropFilter: 'blur(12px)',
    } as any),
  } as any,
  masterAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  } as ViewStyle,
  masterAvatarImage: {
    width: '100%',
    height: '100%',
  } as ImageStyle,
  masterChipTextBlock: {
    alignItems: 'flex-start',
    paddingRight: 8,
  } as ViewStyle,
  masterChipEyebrow: {
    fontSize: 10,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: 'rgba(255,255,255,0.65)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    lineHeight: 14,
  } as TextStyle,
  masterChipTitle: {
    marginTop: 2,
    fontSize: 15,
    fontWeight: '600',
    fontFamily: fonts.display,
    color: '#FFFFFF',
    letterSpacing: 0.1,
    lineHeight: 20,
  } as TextStyle,
  masterChipArrow: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 4,
  } as ViewStyle,
});
